import { useEffect, useState } from 'react';
import { useForm, useWatch, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowRightLeft, Loader2, ServerOff } from 'lucide-react';
import { transferAssetToBranchSchema, type TransferAssetToBranchInput } from '@pms/shared';
import { assetsService, type AssetListItem } from '@/services/assets.service';
import { getErrorMessage } from '@/lib/errors';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { SearchInput } from '@/components/ui/search-input';
import { Notice } from '@/components/ui/notice';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const NONE = '__none__';
const USER_SEARCH_DEBOUNCE_MS = 350;
const USER_PAGE_LIMIT = 100;

interface Props {
  asset: AssetListItem | null;
  onClose: () => void;
}

export function TransferAssetDialog({ asset, onClose }: Props) {
  const queryClient = useQueryClient();
  const [userQ, setUserQ] = useState('');
  const [debouncedUserQ, setDebouncedUserQ] = useState('');

  // The parent keys this dialog by asset id, so each open starts with a fresh form.
  const { control, register, handleSubmit, setValue, formState: { errors } } =
    useForm<TransferAssetToBranchInput>({
      resolver: zodResolver(transferAssetToBranchSchema),
      defaultValues: { assigned_to_user_id: null, assigned_department_id: null, notes: '' },
    });

  const branch = useWatch({ control, name: 'branch' });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedUserQ(userQ), USER_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [userQ]);

  const branchesQuery = useQuery({
    queryKey: ['assets', 'branches'],
    queryFn: () => assetsService.listBranches(),
    enabled: !!asset,
  });

  const usersQuery = useQuery({
    queryKey: ['assets', 'branches', branch, 'users', debouncedUserQ],
    queryFn: () => assetsService.listBranchUsers(branch, { q: debouncedUserQ || undefined, limit: USER_PAGE_LIMIT }),
    enabled: !!asset && !!branch,
  });

  const departmentsQuery = useQuery({
    queryKey: ['assets', 'branches', branch, 'departments'],
    queryFn: () => assetsService.listBranchDepartments(branch),
    enabled: !!asset && !!branch,
  });

  const mutation = useMutation({
    mutationFn: (values: TransferAssetToBranchInput) =>
      assetsService.transferToBranch(asset!.id, {
        ...values,
        notes: values.notes?.trim() || undefined,
      }),
    onSuccess: () => {
      toast.success(`${asset?.asset_code} transferred`);
      void queryClient.invalidateQueries({ queryKey: ['assets', 'list'] });
      void queryClient.invalidateQueries({ queryKey: ['assets', 'branch-transfers'] });
      onClose();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const branches = branchesQuery.data ?? [];
  const branchLabel = branches.find((b) => b.code === branch)?.label;
  const branchDbError = usersQuery.isError || departmentsQuery.isError;

  return (
    <Dialog open={!!asset} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-full max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5 text-primary" />
            Transfer to Branch
          </DialogTitle>
          <DialogDescription>
            <code className="font-mono font-semibold">{asset?.asset_code}</code> moves to the selected
            company with the same code. It leaves this company's asset list and shows under Other Branch.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
          {branchesQuery.isError && (
            <Notice tone="danger" compact icon={ServerOff}
              action={<Button size="sm" variant="outline" type="button" onClick={() => void branchesQuery.refetch()}>Retry</Button>}>
              Could not load branches
            </Notice>
          )}
          {branchesQuery.isSuccess && branches.length === 0 && (
            <Notice tone="warning" compact icon={ServerOff}>
              No other branch database is configured on this server
            </Notice>
          )}

          <div className="space-y-1.5">
            <Label>Branch *</Label>
            <Controller
              control={control}
              name="branch"
              render={({ field }) => (
                <Select
                  value={field.value ?? ''}
                  onValueChange={(v) => {
                    field.onChange(v);
                    setValue('assigned_to_user_id', null);
                    setValue('assigned_department_id', null);
                    setUserQ('');
                  }}
                  disabled={branchesQuery.isLoading || branches.length === 0}
                >
                  <SelectTrigger className={errors.branch ? 'border-destructive' : ''}>
                    <SelectValue placeholder={branchesQuery.isLoading ? 'Loading…' : 'Select branch'} />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map((b) => (
                      <SelectItem key={b.code} value={b.code}>{b.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.branch && <p className="text-xs text-destructive">Select a branch</p>}
          </div>

          {branch && branchDbError && (
            <Notice tone="danger" compact icon={ServerOff}
              action={
                <Button size="sm" variant="outline" type="button"
                  onClick={() => { void usersQuery.refetch(); void departmentsQuery.refetch(); }}>
                  Retry
                </Button>
              }>
              {getErrorMessage(usersQuery.error ?? departmentsQuery.error)}
            </Notice>
          )}

          {branch && !branchDbError && (
            <>
              <div className="space-y-1.5">
                <Label>Assign to ({branchLabel} user)</Label>
                <SearchInput value={userQ} onChange={setUserQ} placeholder="Search name or employee code…" />
                <Controller
                  control={control}
                  name="assigned_to_user_id"
                  render={({ field }) => (
                    <Select
                      value={field.value ?? NONE}
                      onValueChange={(v) => {
                        const id = v === NONE ? null : v;
                        field.onChange(id);
                        const user = usersQuery.data?.data.find((u) => u.id === id);
                        setValue('assigned_department_id', user?.department_id ?? null);
                      }}
                      disabled={usersQuery.isLoading}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={usersQuery.isLoading ? 'Loading users…' : 'Unassigned'} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>Unassigned</SelectItem>
                        {(usersQuery.data?.data ?? []).map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.name}
                            {u.employee_code && <span className="ml-1 text-muted-foreground">· {u.employee_code}</span>}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {usersQuery.data && usersQuery.data.pagination.total > usersQuery.data.data.length && (
                  <p className="text-xs text-muted-foreground">
                    Showing {usersQuery.data.data.length} of {usersQuery.data.pagination.total} — search to narrow down
                  </p>
                )}
                {usersQuery.data && usersQuery.data.data.length === 0 && (
                  <p className="text-xs text-muted-foreground">No users match in {branchLabel}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label>Department</Label>
                <Controller
                  control={control}
                  name="assigned_department_id"
                  render={({ field }) => (
                    <Select
                      value={field.value ?? NONE}
                      onValueChange={(v) => field.onChange(v === NONE ? null : v)}
                      disabled={departmentsQuery.isLoading}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={departmentsQuery.isLoading ? 'Loading…' : 'None'} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>None</SelectItem>
                        {(departmentsQuery.data ?? []).map((d) => (
                          <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="transfer-notes">Notes</Label>
            <Textarea id="transfer-notes" {...register('notes')} rows={2} placeholder="Optional — reason, handover details" />
            {errors.notes && <p className="text-xs text-destructive">{errors.notes.message}</p>}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
            <Button type="submit" disabled={mutation.isPending || !branch || branchDbError}>
              {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Transfer{branchLabel ? ` to ${branchLabel}` : ''}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
